// The approvals queue (SPEC §11, key A, route #/approvals): every task awaiting approval that the
// user can operate on, oldest first, grouped by floor; one draft shown large at a time with its
// editable draft. Keys: A approve, R reject, J/K next/previous, E edit. Live: tasks arriving,
// approved or rejected elsewhere update the queue at once.
import { h, svg, icon, button, iconButton, emptyState, spinner, kbd, setText, setAttr } from '../dom.js';
import { t } from '../i18n/fr.js';
import { deptColor, formatDuration, relativeTime } from '../format.js';
import {
  approvalQueue, queueStep, nextAfterRemoval, waitingKey, sourceInfo, toMs,
} from './tasks/model.js';
import { collapsibleText, agentChip, section } from './tasks/components.js';
import { createApprovalBox } from './tasks/approvalbox.js';
import './tasks/strings.js';

/** The calm illustration of the empty queue: an empty tray, a small plant, a green check. */
function calmIllustration() {
  return svg('svg', { class: 'ap-ill', viewBox: '0 0 160 110', width: 160, height: 110, role: 'img', 'aria-label': t('ap.empty.illustration') },
    svg('ellipse', { class: 'ap-ill-shadow', cx: 80, cy: 96, rx: 62, ry: 8 }),
    svg('path', { class: 'ap-ill-tray', d: 'M34 62h92l-8 28H42z' }),
    svg('path', { class: 'ap-ill-tray-edge', d: 'M30 58h100v6H30z' }),
    svg('path', { class: 'ap-ill-pot', d: 'M118 70h18l-3 20h-12z' }),
    svg('path', { class: 'ap-ill-leaf', d: 'M127 70c0-10-6-16-13-18 1 8 5 14 13 18z' }),
    svg('path', { class: 'ap-ill-leaf', d: 'M127 70c0-12 5-19 12-22 0 9-4 17-12 22z' }),
    svg('circle', { class: 'ap-ill-check-bg', cx: 70, cy: 32, r: 16 }),
    svg('path', { class: 'ap-ill-check', d: 'M62 32.5l5.5 5.5L79 26.5' }));
}

/**
 * How long a draft must have been on screen before the A key approves it: a held key (the one
 * that opened the queue too) or a double tap must never approve the next draft, unread, right
 * after the queue moved on. The APPROUVER button is not delayed.
 */
export const APPROVE_ARM_MS = 800;

/**
 * Opens the approvals queue (key A, route #/approvals).
 * @param {object} ctx app context
 * @param {{armMs?: number}} [opts] armMs: APPROVE_ARM_MS (tests shorten it)
 * @returns {import('./overlay.js').Layer}
 */
export function openApprovals(ctx, { armMs = APPROVE_ARM_MS } = {}) {
  const layer = ctx.overlay.openDrawer({
    eyebrow: t('ap.eyebrow'),
    title: t('ap.title'),
    size: 'xl',
    className: 'ap-drawer',
    content: h('div', { class: 'ap-loading' }, spinner(t('ap.loading')), h('p', { class: 'muted' }, t('ap.loading'))),
  });

  const known = new Map();    // id → summary (awaiting approval)
  const details = new Map();  // id → detail
  const boxes = new Map();    // id → approval box (kept while the task waits, so edits survive J/K)
  const acted = new Set();    // ids this user approved/rejected here
  let list = [];
  let groups = [];
  let currentId = null;
  let handled = 0;
  let unavailable = false;
  let loaded = false;
  let closed = false;
  let detailSeq = 0;
  let renderTimer = null;
  let loadingDetail = false;
  let detailError = null;
  let armedBox = null; // the approval box on screen, and since when (the A key waits APPROVE_ARM_MS)
  let armedAt = 0;
  const offs = [];

  const canOperate = (tk) => ctx.can('operate', { floorId: tk.floorId, deptId: tk.deptId });

  // Seed from the tasks the browser already knows (shown at once, then replaced by the server list).
  for (const tk of ctx.store.get().tasks?.values?.() || []) if (tk?.state === 'awaiting_approval') known.set(tk.id, tk);

  // ------------------------------------------------------------------------------------------
  // Data

  async function loadQueue() {
    try {
      const res = await ctx.api.get(ctx.api.withQuery('/api/tasks', { state: 'awaiting_approval', limit: 200 }));
      if (closed) return;
      const items = Array.isArray(res) ? res : Array.isArray(res?.tasks) ? res.tasks : [];
      known.clear();
      for (const tk of items) if (tk && typeof tk.id === 'string' && tk.state === 'awaiting_approval') known.set(tk.id, tk);
      unavailable = false;
    } catch (err) {
      if (closed) return;
      if (err?.status === 404) unavailable = true;
      else ctx.toast?.error?.(err);
    }
    loaded = true;
    recompute();
    select(currentId && list.some((x) => x.id === currentId) ? currentId : list[0]?.id || null);
  }

  function recompute() {
    const q = approvalQueue(known.values(), { floors: ctx.store.get().office.floors, canOperate });
    groups = q.groups;
    list = q.list;
    for (const id of [...boxes.keys()]) {
      if (!known.has(id)) { boxes.get(id).destroy(); boxes.delete(id); details.delete(id); }
    }
  }

  async function loadDetail(id) {
    const seq = ++detailSeq;
    loadingDetail = true;
    detailError = null;
    renderMain();
    try {
      const d = await ctx.api.get(`/api/tasks/${encodeURIComponent(id)}`);
      if (closed || seq !== detailSeq) return;
      if (!d || typeof d !== 'object' || d.id !== id) throw new Error(t('ap.detailUnavailable'));
      details.set(id, d);
      if (d.state !== 'awaiting_approval') {
        removeFromQueue(id, { elsewhere: true });
        return;
      }
    } catch (err) {
      if (closed || seq !== detailSeq) return;
      if (err?.status === 404 && !unavailable) { removeFromQueue(id, { elsewhere: true }); return; }
      detailError = err;
    } finally {
      if (!closed && seq === detailSeq) loadingDetail = false;
    }
    if (!closed && seq === detailSeq) renderMain();
  }

  function select(id) {
    currentId = id;
    renderAll();
    keepFocus();
    if (!id) return;
    const s = known.get(id);
    const d = details.get(id);
    const fresh = d && s && (toMs(d.updatedAt) ?? 0) >= (toMs(s.updatedAt) ?? 0);
    if (!fresh) loadDetail(id);
  }

  function step(delta) {
    const next = queueStep(list, currentId, delta);
    if (next && next !== currentId) select(next);
  }

  function removeFromQueue(id, { elsewhere = false } = {}) {
    if (!known.has(id) && !boxes.has(id)) return;
    const oldList = list;
    const box = boxes.get(id);
    const wasCurrent = id === currentId;
    if (elsewhere && wasCurrent && !acted.has(id)) ctx.toast?.info?.(t('ap.handledElsewhere'));
    else if (elsewhere && box?.isDirty() && !acted.has(id)) ctx.toast?.info?.(t('ap.handledElsewhere'));
    known.delete(id);
    recompute();
    if (wasCurrent) select(nextAfterRemoval(oldList, list, id));
    else scheduleRender();
  }

  // ------------------------------------------------------------------------------------------
  // Rendering

  const rail = h('nav', { class: 'ap-rail', 'aria-label': t('ap.queueLabel') });
  const main = h('div', { class: 'ap-main' });
  const counter = h('span', { class: 'ap-counter mono-label', 'aria-live': 'polite' });
  const prevBtn = iconButton('chevronLeft', t('ap.prev'), { onClick: () => step(-1) });
  const nextBtn = iconButton('chevronRight', t('ap.next'), { onClick: () => step(1) });
  const keysHint = h('p', { class: 'ap-keys', 'aria-hidden': 'true' },
    kbd('A'), h('span', {}, t('ap.key.approve')), kbd('R'), h('span', {}, t('ap.key.reject')),
    kbd('E'), h('span', {}, t('ap.key.edit')), kbd('J'), kbd('K'), h('span', {}, t('ap.key.nav')));
  const nav = h('div', { class: 'ap-nav' }, prevBtn, counter, nextBtn, h('span', { class: 'spacer' }), keysHint);
  const notice = h('p', { class: 'ap-notice', role: 'note', hidden: true }, icon('info', { size: 14 }), h('span', {}, t('ap.unavailable')));
  const layout = h('div', { class: 'ap-layout' }, rail, h('div', { class: 'ap-stage' }, nav, main));

  function scheduleRender() {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(() => renderAll(), 60);
  }

  function renderAll() {
    if (closed) return;
    clearTimeout(renderTimer);
    const n = list.length;
    layer.setTitle(n ? t('ap.titleCount', { n }) : t('ap.title'));
    notice.hidden = !unavailable;
    if (!loaded && !n) return; // keep the loading view
    if (!n) {
      layer.setFooter?.();
      layer.setContent(notice, h('div', { class: 'ap-empty' }, calmIllustration(),
        h('p', { class: 'ap-empty-title' }, t('ap.empty.title')),
        h('p', { class: 'ap-empty-text' }, handled ? t('ap.empty.done', { n: handled }) : t('ap.empty.text')),
        button(t('common.close'), { variant: 'secondary', onClick: () => layer.close('button') })));
      return;
    }
    if (layer.body && !layer.body.contains?.(layout)) layer.setContent(notice, layout);
    else if (!layer.body) layer.setContent(notice, layout);
    renderRail();
    renderNav();
    renderMain();
  }

  function renderNav() {
    const idx = list.findIndex((x) => x.id === currentId);
    const n = list.length;
    setText(counter, idx >= 0 ? t('ap.counter', { i: idx + 1, n }) : '');
    setAttr(counter, 'aria-label', idx >= 0 ? t('ap.counterAria', { i: idx + 1, n }) : null);
    prevBtn.disabled = idx <= 0;
    nextBtn.disabled = idx < 0 || idx >= n - 1;
  }

  function renderRail() {
    const now = Date.now();
    const { office } = ctx.store.get();
    rail.replaceChildren(...groups.map((g) => {
      const floor = office.floors.find((f) => f.id === g.floorId);
      return h('section', { class: 'ap-group', 'aria-label': g.floorName },
        h('p', { class: 'ap-group-title mono-label', style: { '--floor': deptColor(floor?.color || g.color, g.floorId) } },
          h('span', { class: 'ap-group-dot', 'aria-hidden': 'true' }), h('span', {}, t('ap.floorGroup', { floor: g.floorName, n: g.tasks.length }))),
        h('ul', { class: 'ap-items' }, g.tasks.map((tk) => {
          const agent = office.agents.find((a) => a.id === tk.agentId);
          const since = waitingKey(tk);
          const active = tk.id === currentId;
          return h('li', {}, h('button', {
            type: 'button', class: ['ap-item', { 'is-active': active, 'is-edited': !!boxes.get(tk.id)?.isDirty() }],
            'aria-current': active ? 'true' : null, dataset: { id: tk.id },
            onClick: () => select(tk.id),
          },
          h('span', { class: 'ap-item-title' }, tk.title || t('task.untitled')),
          h('span', { class: 'ap-item-meta mono-label' }, t('ap.agentLine', { agent: agent?.name || tk.agentName || '', dept: tk.deptName || '' })),
          since ? h('span', { class: 'ap-item-time' }, relativeTime(since, now, { short: true })) : null));
        })));
    }));
  }

  function renderMain() {
    if (closed) return;
    const summary = currentId ? known.get(currentId) : null;
    if (!summary) { main.replaceChildren(); layer.setFooter?.(); return; }
    const detail = details.get(currentId);
    const now = Date.now();
    const { office, me } = ctx.store.get();
    const agent = office.agents.find((a) => a.id === summary.agentId) || null;
    const dept = office.departments.find((d) => d.id === summary.deptId) || null;
    const floor = office.floors.find((f) => f.id === summary.floorId) || null;
    const task = detail || summary;
    const src = sourceInfo(task, { meId: me?.id });
    const since = waitingKey(summary);

    let body;
    if (detail && detail.state === 'awaiting_approval') {
      let box = boxes.get(currentId);
      if (box && box.draft !== (detail.draft ?? '')) { box.destroy(); boxes.delete(currentId); box = null; }
      if (!box) {
        const id = currentId;
        box = createApprovalBox(ctx, detail, {
          shortcuts: true,
          onDirtyChange: () => scheduleRailOnly(),
          onBarChange: (shown) => { if (layer.footer && boxes.get(currentId) === box) layer.footer.hidden = !shown; },
          onDone: (kind) => {
            if (kind === 'conflict') { removeFromQueue(id, { elsewhere: false }); return; }
            if (!acted.has(id)) handled += 1;
            acted.add(id);
            removeFromQueue(id);
          },
        });
        boxes.set(currentId, box);
      }
      body = box.el;
      if (armedBox !== box) { armedBox = box; armedAt = Date.now(); }
      // The decision bar of the draft on screen stays in the footer, always visible.
      if (layer.footer?.firstChild !== box.bar) layer.setFooter?.(box.bar);
      if (layer.footer) layer.footer.hidden = box.isRejecting();
    } else if (loadingDetail) {
      layer.setFooter?.();
      body = h('div', { class: 'ap-detail-loading' }, spinner(t('ap.detailLoading')), h('p', { class: 'muted' }, t('ap.detailLoading')));
    } else {
      layer.setFooter?.();
      body = emptyState({
        icon: 'alert', title: t('ap.detailUnavailable'), text: detailError?.message || '',
        action: button(t('td.retryLoad'), { variant: 'secondary', icon: 'refresh', onClick: () => loadDetail(summary.id) }),
      });
    }

    const request = typeof task.input === 'string' ? task.input : task.inputPreview || '';
    main.replaceChildren(h('article', { class: 'ap-card', 'aria-labelledby': 'ap-card-title' },
      h('header', { class: 'ap-card-head' },
        h('h3', { class: 'ap-card-title', id: 'ap-card-title' }, summary.title || t('task.untitled')),
        h('div', { class: 'ap-card-meta' },
          agentChip(ctx, agent, { name: summary.agentName || '' }),
          dept || summary.deptName ? h('span', { class: 'td-dept', style: { '--dept': deptColor(dept?.color, summary.deptId || '') } },
            h('span', { class: 'td-dept-dot', 'aria-hidden': 'true' }),
            h('span', { class: 'td-dept-name' }, t('td.deptFloor', { dept: dept?.name || summary.deptName || '', floor: floor?.name || summary.floorName || '' }))) : null,
          since ? h('span', { class: 'ap-card-since' }, icon('clock', { size: 13 }), t('ap.waitingSince', { duration: formatDuration(Math.max(0, now - since)) })) : null),
        h('p', { class: 'td-source' }, icon(src.icon, { size: 14 }), h('span', {}, src.text))),
      request ? section(t('td.section.request'), collapsibleText(request, { maxLines: 4, maxChars: 400 }), { class: 'td-request' }) : null,
      body,
      h('div', { class: 'ap-card-foot' },
        button(t('ap.openTask'), { variant: 'link', size: 'sm', iconRight: 'arrowRight',
          onClick: () => ctx.navigate(`#/task/${encodeURIComponent(summary.id)}`) }))));
  }

  /** After the shown task changed, keep the keyboard focus inside the drawer (the old card is gone). */
  function keepFocus() {
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    if (!active || active === document.body || !layer.panel?.contains?.(active)) layer.panel?.focus?.({ preventScroll: true });
  }

  let railTimer = null;
  function scheduleRailOnly() {
    clearTimeout(railTimer);
    railTimer = setTimeout(() => { if (!closed && list.length) renderRail(); }, 150);
  }

  // ------------------------------------------------------------------------------------------
  // Keyboard and live updates

  const pop = ctx.keys?.pushScope?.((e, { editable } = {}) => {
    if (closed || editable || e.ctrlKey || e.metaKey || e.altKey) return false;
    if (ctx.overlay.top?.() && ctx.overlay.top() !== layer) return false;
    const key = String(e.key || '').toLowerCase();
    const box = currentId ? boxes.get(currentId) : null;
    // A held key repeats: never act on a repeat (it would approve the next drafts unread).
    if (e.repeat) return ['a', 'r', 'e', 'j', 'k'].includes(key);
    switch (key) {
      case 'j': step(1); return true;
      case 'k': step(-1); return true;
      case 'a':
        if (box && !box.isRejecting() && armedBox === box && Date.now() - armedAt >= armMs) box.approve();
        return !!box;
      case 'r': if (box) box.openReject(); return !!box;
      case 'e': if (box && !box.isRejecting()) box.toggleEdit(); return !!box;
      default: return false;
    }
  });
  if (pop) offs.push(pop);

  offs.push(ctx.events.on('task', (s) => {
    if (!s || typeof s.id !== 'string' || closed) return;
    if (s.state === 'awaiting_approval') {
      const prev = known.get(s.id);
      known.set(s.id, s);
      if (prev && details.has(s.id) && (toMs(s.updatedAt) ?? 0) > (toMs(details.get(s.id).updatedAt) ?? 0)) {
        details.delete(s.id);
        if (s.id === currentId) loadDetail(s.id);
      }
      recompute();
      if (!currentId) select(list[0]?.id || null);
      else scheduleRender();
    } else if (known.has(s.id)) {
      // Our own approve/reject can be announced by the live event before its response arrives:
      // the box is still busy then, and the draft was handled here, not elsewhere.
      if (boxes.get(s.id)?.isBusy?.() && !acted.has(s.id)) { acted.add(s.id); handled += 1; }
      removeFromQueue(s.id, { elsewhere: true });
    }
  }));
  offs.push(ctx.events.on('task.removed', (d) => { if (d?.id && known.has(d.id)) removeFromQueue(d.id, { elsewhere: true }); }));
  offs.push(ctx.events.on('hello', () => { if (!closed) loadQueue(); }));

  layer.onClose(() => {
    closed = true;
    clearTimeout(renderTimer);
    clearTimeout(railTimer);
    for (const box of boxes.values()) box.destroy();
    boxes.clear();
    for (const off of offs.splice(0)) { try { off?.(); } catch { /* ignore */ } }
  });

  recompute();
  if (list.length) select(list[0].id);
  loadQueue();
  return layer;
}
